import { BatchLoadExperimentService } from 'business/services/BatchLoadExperimentService';
import React, { useContext, useEffect, useState } from 'react';
import BatchLoadExperimentWizardContext from './BatchLoadExperimentWizardContext';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import {
  BatchLoadExperimentsData,
  Entry,
  ExperimentDTO,
  ExperimentInfoDTO,
  LayerTypeDTO,
  PrintBarcodeApiResponse,
  ProteinFormulationDTO,
  ProteinLayerDTO,
  ScreenLayerDTO,
  ScreenLotDTO,
  SystemTaskResponseDTO,
  TreeNodeDTO,
} from 'business/models/Models';
import {
  getNewId,
  isNullOrUndefined,
  launchExpPrintBarCodeRequest,
  setSystemStateBusy,
  showAlertSnackBar,
} from 'utils/Global';
import ExperimentService from 'business/services/ExperimentService';
import { initLayer } from 'components/plateviewer/experiment/PasteLayerHelper';
import { getUniqueLayerName } from 'business/plateviewer/GenerateUtil';
import {
  BatchloadTaskResponseDTO,
  ProteinLayerProteinFormulationDetailsDTO,
} from 'business/models/CustomModels';
import { EGridVaryAlong } from 'enums/ELayersView';
import { generate } from 'business/plateviewer/Generate/GenerateCommon';
import WizardService from 'business/services/WizardService';
import { generateBatchLoadExperimentReportPDF } from 'components/reports/BatchLoadExperimentReport';
import { LongRunningTask, PostTaskAction } from 'components/systemTasks/SystemTask';
import { useRMDispatch } from 'redux/hooks';
import { addTask, updateProgress } from 'redux/reducers/systemTaskReducer';
import {
  BroadcastChannelTyped,
  CHANNEL_ACTIONS,
  CHANNEL_TYPES,
} from 'business/broadcast';
import EAlertType from 'enums/EAlertType';
import ESnackBarPosition from 'enums/ESnackBarPosition';
import { LoadingModal } from 'components/modal';
import { removeExperimentFromScreen } from 'utils/ExperimentUtils';
import { useShowLabelPrinterError } from 'CustomHooks';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';

export default function CreateBatchLoadExperiments({ onClose }: any) {
  const [loading, setLoading] = useState<{ show: boolean; text: string }>({
    show: true,
    text: 'Experiments are being created.',
  });
  const [isExperimentsCreated, setIsExperimentsCreated] = useState(false);
  const batchLoadWizardContext = useContext(BatchLoadExperimentWizardContext);
  const dispatch = useRMDispatch();
  const showLabelPrinterError = useShowLabelPrinterError();

  useEffect(() => {
    setTimeout(() => {
      onClose();
    }, 5000);
    const wizardService: WizardService = new WizardService();
    async function saveExperiment(
      expDto,
      expInfo,
      layers,
      parentTreenodeId,
      existingExperimentId?,
    ) {
      layers = removeExperimentFromScreen(layers);
      return await wizardService.saveWizardExperiment(
        expDto,
        expInfo,
        parentTreenodeId,
        layers,
        undefined,
        undefined,
        existingExperimentId,
      );
    }

    const createExps = async (
      validOrWarningExperimentData: BatchLoadExperimentsData[],
      tid: number,
    ) => {
      //Todo: Need to change to use hook come up with a better solution to implement hook to a broadcast channel
      //or  remove hook and use simple function
      const explorerBroadcastChannel = new BroadcastChannelTyped(CHANNEL_TYPES.EXPLORER);
      const batchLoadExperimentService = new BatchLoadExperimentService();
      const validData = validOrWarningExperimentData;
      const experimentIds: number[] = [];
      const systemStateStream = setSystemStateBusy();
      try {
        for (let index = 0; index < validData.length; index++) {
          dispatch(
            updateProgress({
              taskId: tid,
              title:
                'Creating Batchload Experiments (' +
                (index + 1) +
                '/' +
                validData.length +
                ')',
            }),
          );
          const row = validData[index];
          row.folderHierarchyOrder = new Entry({
            value: batchLoadWizardContext.folderHierarchyOrder.join(','),
          });
          const createExperimentInfoResponse =
            await batchLoadExperimentService.createBatchLoadExperiments(row);

          const body = {
            experimentInfo: createExperimentInfoResponse.experimentInfo,
            paretnTreeNodeId: createExperimentInfoResponse.parentTreeNodeId,
            save: true,
          };
          const nonPersistedExperimentResponse =
            await batchLoadExperimentService.CreateNonPersistedExperiment(body);

          if (!nonPersistedExperimentResponse?.value) {
            throw new Error(
              nonPersistedExperimentResponse?.message ?? 'Failed to create experiment.',
            );
          }
          const expDtoClient: ExperimentDtoClient =
            nonPersistedExperimentResponse.value as any;
          if (
            !isNullOrUndefined(row.plateBarcode.value.trim()) &&
            expDtoClient.plateDto?.[0]
          ) {
            expDtoClient.plateDto[0].barCode = row.plateBarcode.value.trim();
          }

          const globalPFList: ProteinFormulationDTO[] =
            createExperimentInfoResponse.proteinFormulationList;

          const serv: ExperimentService = new ExperimentService();
          const screenLotDetails: ScreenLotDTO = await serv.getScreenLotDetails(
            createExperimentInfoResponse.experimentInfo.screenInfo.screenLotId,
          );
          expDtoClient.layers = [];
          const { newLayer: screenLayer, layerDetail: screenLayerDetails } = initLayer(
            -1,
            LayerTypeDTO.Screen,
            expDtoClient,
            0,
          );
          (screenLayerDetails as ScreenLayerDTO).screenLot = screenLotDetails;
          screenLayer.layerDetail = screenLayerDetails;
          screenLayer.volumeBasedAddition = false;

          createExperimentInfoResponse.dropProtiens.forEach((sampleProtein, index) => {
            const dropProtein = sampleProtein.proteinInfo;
            if (isNullOrUndefined(dropProtein)) {
              return;
            }
            if (isNullOrUndefined(dropProtein.name)) {
              return;
            }
            const { newLayer: proteinLayer, layerDetail: proteinLayerDetail } = initLayer(
              -1,
              LayerTypeDTO.Protein,
              expDtoClient,
              0,
            );
            const layerName = getUniqueLayerName(expDtoClient.layers, dropProtein.name);
            proteinLayer.name = layerName;
            const proteinFormulation = globalPFList.find(
              (pf) => pf.treeNodeId == dropProtein.id,
            );
            proteinLayer.volumeBasedAddition = true;
            proteinLayer.layerDetail = proteinLayerDetail;
            proteinLayer.targetDrop = sampleProtein.dropNumber;
            const plDTO: ProteinLayerDTO = proteinLayerDetail;

            let plPFDTO: ProteinLayerProteinFormulationDetailsDTO;
            if (
              plDTO.proteinLayerProteinFormulation === undefined ||
              plDTO.proteinLayerProteinFormulation.length === 0
            ) {
              plPFDTO = new ProteinLayerProteinFormulationDetailsDTO();
              plDTO.proteinLayerProteinFormulation = [];
              plDTO.proteinLayerProteinFormulation.push(plPFDTO);
            } else {
              plPFDTO = plDTO.proteinLayerProteinFormulation[0];
            }
            const drop = row.drops.find(
              (d) => parseInt(d.dropNumber.value) == sampleProtein.dropNumber,
            );

            plDTO.layerId = proteinLayer.id;
            plPFDTO.proteinFormulationId = proteinFormulation.id;
            plPFDTO.proteinDropVolumeVaryAlong = EGridVaryAlong.NONE;
            plPFDTO.startProteinDropVolume = parseFloat(drop.sampleWellRatio.value);
            plPFDTO.stopProteinDropVolume = parseFloat(drop.sampleWellRatio.value);
            plPFDTO.wellDropVolumeVaryAlong = EGridVaryAlong.NONE;
            plPFDTO.startWellDropVolume = parseFloat(drop.dropVolume.value);
            plPFDTO.stopWellDropVolume = parseFloat(drop.dropVolume.value);
            const pfdto = new ProteinFormulationDTO();
            pfdto.id = proteinFormulation.id;
            plPFDTO.proteinFormulation = pfdto;

            plPFDTO.proteinFormulationId = proteinFormulation.id;
            expDtoClient.layers.push(proteinLayer);

            if (row.seedId?.value) {
              const { newLayer: seedLayer, layerDetail: seedLayerDetail } = initLayer(
                -1,
                LayerTypeDTO.Seed,
                expDtoClient,
                0,
              );
              seedLayerDetail.seedId = parseInt(row.seedId.value);
              seedLayerDetail.startSeedDropVolume = 0;
              seedLayerDetail.stopSeedDropVolume = 0;
              seedLayerDetail.seedDropVolumeVaryAlong = EGridVaryAlong.NONE;
              seedLayer.layerDetail = seedLayerDetail;
              seedLayer.targetDrop = sampleProtein.dropNumber;
              expDtoClient.layers.push(seedLayer);
            }
          });
          expDtoClient.layers.push(screenLayer);

          generate(expDtoClient, false);
          const expDto: ExperimentDTO = new ExperimentDTO();
          expDto.init(expDtoClient);

          if (row.experimentNotes?.value) {
            expDto.info = new ExperimentInfoDTO();
            expDto.info.notes = row.experimentNotes.value;
          }

          const savedExpId = await saveExperiment(
            expDto,
            createExperimentInfoResponse.experimentInfo,
            expDtoClient.layers,
            createExperimentInfoResponse.parentTreeNodeId,
            expDtoClient.experimentId,
          );

          const expIds = experimentIds;
          expIds.push(savedExpId.value);
          dispatch(
            updateProgress({
              taskId: tid,
              progress: ((index + 1) / validData.length) * 90,
            }),
          );
          if (savedExpId == null) {
            return;
          }

          const treeNode = {
            id: savedExpId.value,
            parentId: createExperimentInfoResponse.parentTreeNodeId,
            name: expDto.expName,
            type: ETreeNodeTypes.Experiment,
          } as TreeNodeDTO;

          explorerBroadcastChannel.postMessage({
            action: CHANNEL_ACTIONS.EXPLORER_ADD_NODE,
            payload: treeNode,
          });
        }
      } finally {
        (await systemStateStream).dispose();
      }

      explorerBroadcastChannel.close();
      const result = new BatchloadTaskResponseDTO();
      result.experimentIds = experimentIds;
      const taskResponse = new SystemTaskResponseDTO();
      taskResponse.success = true;
      taskResponse.result = result;
      return taskResponse;
    };

    const task: LongRunningTask = {
      id: getNewId(),
      iconProps: { name: 'BatchloadTask', fill: 'none' },
      title: 'Creating Batchload Experiments',
      completionTitle:
        'Created Batchload Experiments (' +
        batchLoadWizardContext.experimentData.length +
        ')',
      param: batchLoadWizardContext.experimentData,
      serviceMathod: createExps,
      progress: 1,
      postActions: [
        {
          titleTip: 'Report',
          iconProps: { name: 'BatchloadExperimentReport', fill: 'none' },
          method: printReportAction,
          param: {
            validOrWarningExperimentData: batchLoadWizardContext.experimentData,
            fileName: batchLoadWizardContext.fileName,
            batchLoadExperimentsDTO: batchLoadWizardContext.batchLoadExperimentsDTO,
          },
        } as PostTaskAction,
        {
          titleTip: 'Print Barcode',
          iconProps: { name: 'PrintBatchLoadExpBarcode', fill: 'none' },
          method: printBarcodeAction,
          param: {},
        } as PostTaskAction,
      ],
    } as LongRunningTask;
    dispatch(addTask(task));
    setLoading({ show: false, text: '' });
    setIsExperimentsCreated(true);
  }, []);

  const printReportAction = async (param, actionResult) => {
    await generateBatchLoadExperimentReportPDF(
      param.validOrWarningExperimentData,
      actionResult.experimentIds,
      param.fileName,
      param.batchLoadExperimentsDTO,
    );
  };

  const printBarcodeAction = async (param, actionResult) => {
    await printBatchloadExpBarcode(actionResult.experimentIds);
  };

  const printBatchloadExpBarcode = async (experimentIds: number[]) => {
    showAlertSnackBar(
      EAlertType.INFO,
      ESnackBarPosition.TOP,
      'Experiment barcodes have been sent for printing.',
    );
    const printResponses: PrintBarcodeApiResponse[] = await launchExpPrintBarCodeRequest(
      experimentIds,
    );
    const responsesWithContent = printResponses?.filter(
      (r) => r.value && r.value.trim() !== '',
    );

    if (responsesWithContent && responsesWithContent.length) {
      const allErrorMessages = responsesWithContent.map((r) => r.value).join('\n');

      console.error(allErrorMessages);
      showLabelPrinterError();
    } else {
      const truncMsg = printResponses?.find((r) => r.infoMessage?.trim())?.infoMessage;
      if (truncMsg) {
        showAlertSnackBar(EAlertType.WARNING, ESnackBarPosition.TOP, truncMsg);
      }
    }
  };

  return (
    <>
      {isExperimentsCreated && (
        <>
          <div id="createbatchloadexperiment-message">
            Experiment(s) are being Created. Please check the progress in notification
            panel. The wizard is going to close.
          </div>
        </>
      )}
      <LoadingModal showModal={loading.show} text={loading.text} />
    </>
  );
}
